import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { css } from "styled-components";
import { useMapContext, useMapLevel, useMapViewActions } from "@evergis/react";
import { AnimationEndEvent } from "@evergis/sgis/es/Map";
import { Popover, Slider } from "@evergis/ui";

import { TextContainer } from "../../styled";
import { SliderContainer, ZoomLevelContainer, ZoomLevelText } from "./styled";
import { useToggle } from "../../../../hooks/useToggle";
import { useDefaultScaleRange } from "../../../../hooks/style/useDefaultScaleRange";

const CONTENT_MIXIN = css`
  transform: translate(calc(-50% + 0.25rem), calc(-100% - 0.5rem)) scale(1, 1) !important;
`;

export const ZoomLevel: FC = () => {
  const { t } = useTranslation("mapControls");
  const { map } = useMapContext();
  const level = useMapLevel();
  const { changeZoomLevel } = useMapViewActions();
  const [isOpen, toggleOpen] = useToggle(false);
  const [zoomLevel, setZoomLevel] = useState(level || 0);
  const animationStopped = useRef(true);
  const defaultScaleRange = useDefaultScaleRange();

  const onChangeMapZoom = useCallback(
    newZoomLevel => {
      animationStopped.current = false;
      map.once(AnimationEndEvent.type, () => {
        animationStopped.current = true;
      });
      changeZoomLevel(newZoomLevel);
    },
    [map, changeZoomLevel],
  );

  useEffect(() => {
    if (animationStopped.current) {
      setZoomLevel(level);
    }
  }, [level]);

  return (
    <Popover
      anchor={
        <ZoomLevelText title={t("magnificationBar")} onClick={toggleOpen} buttonText width={"2rem"}>
          {level || 0}
        </ZoomLevelText>
      }
      autoPosition={false}
      animateAppearance
      targetOrigin="top"
      anchorOrigin="top"
      open={isOpen}
      onRequestClose={toggleOpen}
      closeOnScroll={false}
      zIndex={99}
      mixin={CONTENT_MIXIN}
    >
      <ZoomLevelContainer withPadding>
        <TextContainer buttonText>{defaultScaleRange[1]}</TextContainer>
        <SliderContainer>
          <Slider
            onChange={newZoomLevel => {
              setZoomLevel(newZoomLevel);
            }}
            onStop={onChangeMapZoom}
            value={zoomLevel}
            min={defaultScaleRange[0]}
            max={defaultScaleRange[1]}
            vertical
            step={1}
            sliderWidth={180}
            tooltipPosition={"right"}
          />
        </SliderContainer>
        <TextContainer buttonText>{defaultScaleRange[0]}</TextContainer>
      </ZoomLevelContainer>
    </Popover>
  );
};
